import {useMutation,useQuery,useQueryClient} from "@tanstack/react-query";
import {api} from "../client";

export type HolidayType = "national"| "regional"| "company" | "optional";

export interface HolidayRow {
    id: string;
    name: string;
    date: string;
    type: HolidayType;
    description: string;
    status: "active" | "inactive";
    createdByFullName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface HolidayListResponse {
  results: HolidayRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

 
export interface HolidayInput {
  name: string;
  date: string; // "YYYY-MM-DD"
  type: HolidayType;
  description?: string;
}

export interface HolidayListParams {
  status?: "active" | "inactive" | "all" | "";
  year?: number | string;
  month?: number | string;
  type?: HolidayType | "";
  search?: string;
}



// Query keys 
 
export const holidaysListKey = (params: HolidayListParams = {}) =>
  ["holidays", "list", params] as const;
 
export const holidayDetailKey = (id: string) =>
  ["holidays", "detail", id] as const;
 
// Hooks 
 
export function useHolidays(params: HolidayListParams = {}) {
  return useQuery<HolidayListResponse>({
    queryKey: holidaysListKey(params),
    queryFn: () =>
      api
        .get<HolidayListResponse>("/holidays/", {
          params: {
            status: params.status || undefined,
            year: params.year || undefined,
            month: params.month || undefined,
            type: params.type || undefined,
            search: params.search || undefined,
          },
        })
        .then((r) => r.data),
  });
}
 
export function useHoliday(id: string | undefined) {
  return useQuery<HolidayRow>({
    queryKey: holidayDetailKey(id ?? ""),
    queryFn: () =>
      api.get<HolidayRow>(`/holidays/${id}/`).then((r) => r.data),
    enabled: !!id,
  });
}
 
export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HolidayInput) =>
      api.post<HolidayRow>("/holidays/", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useUpdateHoliday(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<HolidayInput>) =>
      api.patch<HolidayRow>(`/holidays/${id}/`, input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/holidays/${id}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useActivateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<HolidayRow>(`/holidays/${id}/toggle-status/`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useDeactivateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<HolidayRow>(`/holidays/${id}/toggle-status/`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}

// Local YYYY-MM-DD so the query key flips at local midnight, not UTC.
function localISO(d: Date): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function useTodayHoliday(enabled = true) {
  // Key includes today's local date so cache auto-invalidates across the
  // day boundary — fixes the "page open across midnight still shows
  // yesterday's holiday" / "stale today=holiday after creating a future
  // holiday" cache bug.
  const todayKey = localISO(new Date());
  return useQuery<HolidayRow | null>({
    queryKey: ["holidays", "today", todayKey],
    queryFn: () =>
      api.get<HolidayRow | null>("/holidays/today/").then((r) => r.data),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export interface UpcomingHolidaysResponse {
  results: HolidayRow[];
}

export function useUpcomingHolidays(
  params: { limit?: number; days?: number } = {},
  enabled = true,
) {
  const todayKey = localISO(new Date());
  return useQuery<UpcomingHolidaysResponse>({
    queryKey: ["holidays", "upcoming", todayKey, params],
    queryFn: () =>
      api
        .get<UpcomingHolidaysResponse>("/holidays/upcoming/", {
          params: {
            limit: params.limit ?? undefined,
            days:  params.days  ?? undefined,
          },
        })
        .then((r) => r.data),
    enabled,
    staleTime: 5 * 60_000,
  });
}